import type { ProjectPathParams, UpdateProjectBody } from '@si/types';
import { postForm } from '@si/utils';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';

import { queryKeys } from './queryKeys';

type Params = ProjectPathParams & UpdateProjectBody & { contextImg?: File; orientationImg?: File };

export function useUpdateProject() {
  const queryClient = useQueryClient();

  const { mutate, error, isPending, isSuccess, reset } = useMutation({
    mutationFn: ({ projectId, formData }: { projectId: string; formData: FormData }) =>
      postForm({
        url: `/projects/${projectId}`,
        body: formData,
      }),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        predicate: ({ queryKey }) => {
          if (!Array.isArray(queryKey) || queryKey[0] !== 'project') {
            return false;
          }

          const queryParams = queryKey[1] as { projectId?: string } | undefined;
          return queryParams?.projectId === variables.projectId;
        },
      });
      queryClient.invalidateQueries({
        predicate: ({ queryKey }) => Array.isArray(queryKey) && queryKey[0] === 'projects',
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.getProjectContextImg(variables.projectId),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.getProjectOrientationImg(variables.projectId),
      });
    },
  });

  const updateProject = useCallback(
    (params: Params) => {
      const formData = new FormData();
      formData.append('projectData', JSON.stringify(params));
      if (params.contextImg) {
        formData.append('contextImg', params.contextImg);
      }
      if (params.orientationImg) {
        formData.append('orientationImg', params.orientationImg);
      }

      mutate({ projectId: params.projectId, formData });
    },
    [mutate]
  );

  return {
    updateProject,
    error,
    isPending,
    isSuccess,
    reset,
  };
}
